import { Fragment, useMemo } from 'react';
import { useLoaderData, useSearchParams } from 'react-router-dom';
import Header from '@features/content/Header';
import FilterWrap from '@components/contents/common/FilterWrap';
import { apiSearchSlice } from '@features/api/apiSearchSlice';
import { ALIAS, CUR_NETWORK, FILTER_DATA, NETWORK, SEARCH_PARAMS } from '@constants/index';
import { setRequestToSearchParams } from '@utils/index';
import { useOption } from '@hooks/useOption';
import { getLocalData } from '@features/api/apiLocalSlice';
import { useAppSelector } from '@hooks/defineRtkTypedHook';
import { selectL10N, setAlias } from '@features/ui/UiSlice';
import NoData from '../../components/contents/common/NoData';
import CharacterBnsArticle from '@components/contents/article/CharacterBnsArticle';
import Paging from '@components/contents/common/Paging';
import type { ICharacterBns, IContentData, ILoaderData, l10nFilterType } from '@type/index';

export const CharacterBnsLoader =
  (dispatch, option: IOption) =>
  async ({ request }): Promise<ICharacterBns | null> => {
    const searchParams = setRequestToSearchParams(request);

    // alias 변경 - searchParams 변경에 사용됨
    dispatch(setAlias(ALIAS.CHARACTER));

    if (!searchParams?.keyword) {
      return null;
    }

    for (let key in searchParams) {
      !searchParams[key] && delete searchParams[key]; // serverId 등 string으로 보내면 안됨
    }

    return CUR_NETWORK === NETWORK.OPDEV
      ? await getLocalData(`${ALIAS.CHARACTER}Bns`, searchParams)
      : await dispatch(apiSearchSlice?.endpoints?.getCharacter?.initiate({ option, params: searchParams }));
  };

const setFilterData = (option: IOption) => {
  const { serverData, classData, serviceAlias } = option || {};

  if (!serviceAlias) {
    return null;
  }

  const filterData = FILTER_DATA[serviceAlias][ALIAS.CHARACTER];
  // TODO. 블소 직업 필터 데이터 비동기 호출할 수도 있음. 로직 수정 필요할 수 있음

  let newServerIdData = [...filterData[SEARCH_PARAMS.SERVER_ID]] as l10nFilterType;
  let newClassIdData = [...filterData[SEARCH_PARAMS.CLASS_ID]] as l10nFilterType;

  if (Array.isArray(serverData) && Array.isArray(newServerIdData)) {
    serverData?.forEach((v) => {
      const { serverName, serverId } = v || {};
      newServerIdData?.push({
        title: serverName,
        value: serverId,
      });
    });
  }

  if (Array.isArray(classData) && Array.isArray(newClassIdData)) {
    classData?.forEach((v) => {
      const { name, id } = v || {};
      newClassIdData?.push({
        title: name,
        value: id,
      });
    });
  }

  return {
    ...filterData,
    [SEARCH_PARAMS.SERVER_ID]: newServerIdData,
    [SEARCH_PARAMS.CLASS_ID]: newClassIdData,
  };
};

interface IFilterComponentProps {
  count: number;
}
const FilterComponent = ({ count }: IFilterComponentProps) => {
  const option = useOption();
  const filterDataObj = useMemo(() => (option ? setFilterData(option) : {}), [option]);

  return (
    <Header totalCount={count}>
      <FilterWrap filterDataObj={filterDataObj} />
    </Header>
  );
};

interface IContentComponentProps {
  data: IContentData<ICharacterBns>;
}
const ContentComponent = ({ data }: IContentComponentProps) => {
  const l10n = useAppSelector(selectL10N);

  return (
    <Fragment>
      <div className="search-content">
        <ul className="column column-2">
          {data?.list.map((item, i) => (
            <li key={i} className="search-item">
              <CharacterBnsArticle item={item} l10n={l10n!} />
            </li>
          ))}
        </ul>
      </div>
      <Paging endPage={data?.pagination?.endPage} showPage={5} l10n={l10n!} />
    </Fragment>
  );
};

const CharacterBns = () => {
  const { data, isLoading, isError } = (useLoaderData() as ILoaderData<IContentData<ICharacterBns>>) || {};

  console.log('Character Data', data);

  return (
    <div className="search-result character character-bns">
      <FilterComponent count={data?.pagination?.total} />
      {!isError && Array.isArray(data?.list) && data?.list?.length > 0 ? <ContentComponent data={data} /> : <NoData />}
    </div>
  );
};

export default CharacterBns;
